import { defineStore } from "pinia";
import { ref, computed } from "vue";
import academicYearApi, {
    type AcademicYear,
    type AcademicYearFormData,
} from "@/api/academicYear";

export const useAcademicYearStore = defineStore("academicYear", () => {
    // State
    const academicYears = ref<AcademicYear[]>([]);
    const currentAcademicYear = ref<AcademicYear | null>(null);
    const loading = ref(false);
    const error = ref<string | null>(null);
    const page = ref(1);
    const perPage = ref(10);
    const search = ref("");
    const isActive = ref<boolean | undefined>(undefined);

    // Getters
    const totalPages = computed(() => meta.value?.last_page || 0);
    const paginatedData = computed(() => academicYears.value);
    const filteredAcademicYears = computed(() => {
        if (!search.value) return academicYears.value;

        return academicYears.value.filter((academicYear: AcademicYear) =>
            academicYear.name
                .toLowerCase()
                .includes(search.value.toLowerCase()),
        );
    });
    const hasAcademicYears = computed(() => academicYears.value.length > 0);
    const isLoading = computed(() => loading.value);
    const meta = ref<{
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
        from?: number;
        to?: number;
    } | null>(null);

    // Actions
    const fetchAcademicYears = async (params?: {
        page?: number;
        per_page?: number;
        search?: string;
        is_active?: boolean;
    }) => {
        loading.value = true;
        error.value = null;

        try {
            const requestParams: any = {
                page: params?.page || page.value,
                per_page: params?.per_page || perPage.value,
                search: params?.search || search.value || undefined,
            };

            // Explicitly handle is_active to ensure boolean false is not filtered out
            if (params?.is_active !== undefined) {
                requestParams.is_active = params?.is_active;
            } else if (isActive.value !== undefined) {
                requestParams.is_active = isActive.value;
            }

            const response: any =
                await academicYearApi.getAcademicYears(requestParams);

            academicYears.value = response.data?.items || response.data || [];
            if (response.data?.meta || response.meta) {
                meta.value = response.data?.meta || response.meta || null;
            }
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to fetch academic years";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const fetchAcademicYear = async (id: string) => {
        loading.value = true;
        error.value = null;

        try {
            const response = await academicYearApi.getAcademicYear(id);
            currentAcademicYear.value = response.data;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to fetch academic year";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const createAcademicYear = async (data: AcademicYearFormData) => {
        loading.value = true;
        error.value = null;

        try {
            const response = await academicYearApi.createAcademicYear(data);
            academicYears.value.unshift(response.data);
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to create academic year";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const updateAcademicYear = async (
        id: string,
        data: AcademicYearFormData,
    ) => {
        loading.value = true;
        error.value = null;

        try {
            const response = await academicYearApi.updateAcademicYear(id, data);
            const index = academicYears.value.findIndex((ay) => ay.id === id);
            if (index !== -1) {
                academicYears.value[index] = response.data;
            }
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to update academic year";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const deleteAcademicYear = async (id: string) => {
        loading.value = true;
        error.value = null;

        try {
            await academicYearApi.deleteAcademicYear(id);
            academicYears.value = academicYears.value.filter(
                (academicYear) => academicYear.id !== id,
            );
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to delete academic year";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const setActiveAcademicYear = async (id: string) => {
        loading.value = true;
        error.value = null;

        try {
            const response = await academicYearApi.setActiveAcademicYear(id);
            const index = academicYears.value.findIndex((ay) => ay.id === id);
            if (index !== -1) {
                academicYears.value[index] = response.data;
            }
        } catch (err: any) {
            error.value =
                err.response?.data?.message ||
                "Failed to set active academic year";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const getActiveAcademicYear = async () => {
        loading.value = true;
        error.value = null;

        try {
            const response = await academicYearApi.getActiveAcademicYear();
            currentAcademicYear.value = response.data;
        } catch (err: any) {
            error.value =
                err.response?.data?.message ||
                "Failed to get active academic year";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const clearError = () => {
        error.value = null;
    };

    const setPage = (newPage: number) => {
        page.value = newPage;
        fetchAcademicYears();
    };

    const setPerPage = (newPerPage: number) => {
        perPage.value = newPerPage;
        page.value = 1;
        fetchAcademicYears();
    };

    const resetFilters = () => {
        page.value = 1;
        perPage.value = 10;
        search.value = "";
        isActive.value = undefined;
        fetchAcademicYears();
    };

    return {
        // State
        academicYears,
        currentAcademicYear,
        loading,
        error,
        page,
        perPage,
        search,
        isActive,
        meta,

        // Getters
        totalPages,
        paginatedData,
        filteredAcademicYears,
        hasAcademicYears,
        isLoading,

        // Actions
        fetchAcademicYears,
        fetchAcademicYear,
        createAcademicYear,
        updateAcademicYear,
        deleteAcademicYear,
        setActiveAcademicYear,
        getActiveAcademicYear,
        clearError,
        setPage,
        setPerPage,
        resetFilters,
    };
});
